// Awards API Helper
// This file provides helper functions for working with the awards API

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export const awardsAPI = {
  // Get award templates for a specific sport
  getTemplates: async (sport, options = {}) => {
    try {
      const { category, awardType, isActive } = options;
      let url = `${API_BASE_URL}/api/awards/templates/sport/${sport}`;
      
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (awardType) params.append('awardType', awardType);
      if (isActive !== undefined) params.append('isActive', isActive);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      return data.success ? data.templates : [];
    } catch (error) {
      console.error('Error fetching award templates:', error);
      return [];
    }
  },

  // Get award templates by sport and category
  getTemplatesByCategory: async (sport, category) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/templates/sport/${sport}/category/${category}`);
      const data = await response.json();
      return data.success ? data.templates : [];
    } catch (error) {
      console.error('Error fetching award templates by category:', error);
      return [];
    }
  },

  // Get award templates by sport and type
  getTemplatesByType: async (sport, type) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/templates/sport/${sport}/type/${type}`);
      const data = await response.json();
      return data.success ? data.templates : [];
    } catch (error) {
      console.error('Error fetching award templates by type:', error);
      return [];
    }
  },

  // Get awards for a program
  getProgramAwards: async (programId, options = {}) => {
    try {
      const { eventId, userId, awardType, category, limit = 20, offset = 0 } = options;
      let url = `${API_BASE_URL}/api/awards/program/${programId}`;
      
      const params = new URLSearchParams();
      if (eventId) params.append('eventId', eventId);
      if (userId) params.append('userId', userId);
      if (awardType) params.append('awardType', awardType);
      if (category) params.append('category', category);
      if (limit) params.append('limit', limit);
      if (offset) params.append('offset', offset);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      return data.success ? data : { awards: [], count: 0 };
    } catch (error) {
      console.error('Error fetching program awards:', error);
      return { awards: [], count: 0 };
    }
  },

  // Get awards for a specific event
  getEventAwards: async (programId, eventId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/program/${programId}/event/${eventId}`);
      const data = await response.json();
      return data.success ? data.awards : [];
    } catch (error) {
      console.error('Error fetching event awards:', error);
      return [];
    }
  },

  // Get awards for a specific user in a program
  getUserAwards: async (programId, userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/program/${programId}/user/${userId}`);
      const data = await response.json();
      return data.success ? data.awards : [];
    } catch (error) {
      console.error('Error fetching user awards:', error);
      return [];
    }
  },

  // Create a new award
  createAward: async (programId, awardData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/program/${programId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(awardData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to create award');
      }
      
      return data;
    } catch (error) {
      console.error('Error creating award:', error);
      throw error;
    }
  },

  // Update an existing award
  updateAward: async (awardId, awardData, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/${awardId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(awardData)
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to update award');
      }
      
      return data;
    } catch (error) {
      console.error('Error updating award:', error);
      throw error;
    }
  },

  // Delete an award (soft delete)
  deleteAward: async (awardId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/${awardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to delete award');
      }
      
      return data;
    } catch (error) {
      console.error('Error deleting award:', error);
      throw error;
    }
  },

  // Get user award statistics
  getUserStatistics: async (userId, programId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/statistics/user/${userId}/program/${programId}`);
      const data = await response.json();
      return data.success ? data.statistics : null;
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      return null;
    }
  },

  // Get program award statistics (leaderboard)
  getProgramStatistics: async (programId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/statistics/program/${programId}`);
      const data = await response.json();
      return data.success ? data.statistics : [];
    } catch (error) {
      console.error('Error fetching program statistics:', error);
      return [];
    }
  },

  // Get team award statistics
  getTeamStatistics: async (teamId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/awards/statistics/team/${teamId}`);
      const data = await response.json();
      return data.success ? data.statistics : [];
    } catch (error) {
      console.error('Error fetching team statistics:', error);
      return [];
    }
  }
};

export default awardsAPI;
